import {
  Avatar,
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import React, { useCallback, useContext } from 'react';
import { format } from 'date-fns';
import {
  MultilineText,
  displayNameInMessage,
  nameInitials,
} from '@xolv-mybrightlink/common';
import { Message } from '../../../type';
import { useUser } from '../../../hooks/store/useUser';
import ErrorIcon from '@mui/icons-material/Warning';
import RetryIcon from '@mui/icons-material/Replay';
import { MediaErrorMessageAwareContext } from './MediaErrorMessageAware';

type TheirMediaErrorMessageProps = {
  message: Message;
};

export const TheirMediaErrorMessage = React.forwardRef<
  HTMLLIElement,
  TheirMediaErrorMessageProps
>((props: TheirMediaErrorMessageProps, ref) => {
  const author = useUser(props.message.authorGuid);
  const avatar = author ? nameInitials(author) : '';
  const authorName = author ? displayNameInMessage(author) : '';
  const formattedTime = format(props.message.createdDate.toDate(), 'HH:mm a');
  const errorAwareContext = useContext(MediaErrorMessageAwareContext);
  const reloadMessage = useCallback(() => {
    if (errorAwareContext) {
      errorAwareContext.retry();
    }
  }, [errorAwareContext]);
  return (
    <ListItem
      ref={ref}
      sx={{ alignItems: 'flex-start', flexWrap: 'nowrap', py: 2, px: 0 }}
    >
      <ListItemAvatar sx={{ minWidth: '42px' }}>
        <Avatar
          sx={{
            bgcolor: '#626a7a',
            width: 32,
            height: 32,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {avatar}
          </Box>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{
          flex: 'unset',
          m: 0,
        }}
        primary={
          <MultilineText maxLines={1} sx={{ fontSize: 14, fontWeight: 500 }}>
            {authorName}
          </MultilineText>
        }
        secondary={
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                borderRadius: 3,
                width: 200,
                height: 112.5,
                bgcolor: '#444',
              }}
            >
              <IconButton
                sx={{ position: 'absolute', alignSelf: 'center', p: 3 }}
                onClick={reloadMessage}
              >
                <RetryIcon sx={{ color: 'white', width: 32, height: 32 }} />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                alignSelf: 'flex-end',
                pl: 2.5,
              }}
            >
              <ErrorIcon
                sx={{ with: 20, height: 20, color: '#d00070', mb: 2.5 }}
              />
              <Box sx={{ textAlign: 'end', color: '#898f9b' }}>
                {formattedTime}
              </Box>
            </Box>
          </Box>
        }
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{
          component: 'div',
          sx: { display: 'flex', mt: 1 },
        }}
      />
    </ListItem>
  );
});

TheirMediaErrorMessage.displayName = 'TheirMediaErrorMessage';
